import { ApiService } from '../ApiService';
import { DiscoverEventsView, EventBasic, EventsView } from '~/models/Event';
import { EventDetails } from '~/models/EventDetails';
import { MemberDetails } from '~/models/MemberDetails';
import { plainToClass } from '~/models/transform';
import { BaseListRequestParams } from '~/models/BaseRequestParams';

export interface NearbyEventsParams extends BaseListRequestParams {
  lat?: number;
  lng?: number;
}

export interface EventCreateParams {
  Code: string;
  Name: string;
  Details?: string;
  StartDate: string;
  EndDate?: string;
  TimesShown: boolean;
  Address?: string;
  Website?: string;
  CreatorIsOrganizer: boolean;
  LocationLatLng?: string;
}

export type EventUpdateParams = Partial<EventCreateParams> & {
  ClearEndDate?: boolean;
};

export class EventsService {
  /**
   * Fetches consolidated event lists for the discover screen.
   * @param take Number of items per list
   */
  static async getDiscoverEvents(take = 9): Promise<DiscoverEventsView> {
    const response = await ApiService.get<DiscoverEventsView>('events/discover', { take });
    return plainToClass(DiscoverEventsView, response);
  }

  /**
   * Fetches upcoming events sorted by start date.
   * @param params Pagination parameters
   */
  static async getUpcomingEvents(params?: BaseListRequestParams): Promise<EventsView> {
    const response = await ApiService.get<EventsView>('events/upcoming', params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches upcoming events near the specified coordinates.
   * @param params Pagination and location parameters
   */
  static async getNearbyEvents(params?: NearbyEventsParams): Promise<EventsView> {
    const response = await ApiService.get<EventsView>('events/nearby', params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches upcoming events that members in the user's network are attending.
   * @param params Pagination parameters
   */
  static async getNetworkEvents(params?: BaseListRequestParams): Promise<EventsView> {
    const response = await ApiService.get<EventsView>('events/network', params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches past events sorted by start date descending.
   * @param params Pagination parameters
   */
  static async getPastEvents(params?: BaseListRequestParams): Promise<EventsView> {
    const response = await ApiService.get<EventsView>('events/past', params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches upcoming events a member is attending.
   * @param memberId Optional member ID (defaults to current user)
   * @param params Pagination parameters
   */
  static async getAttendingEvents(memberId?: number, params?: BaseListRequestParams): Promise<EventsView> {
    const endpoint = memberId ? `events/attending/${memberId}` : 'events/attending';
    const response = await ApiService.get<EventsView>(endpoint, params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches past events a member has attended.
   * @param memberId Optional member ID (defaults to current user)
   * @param params Pagination parameters
   */
  static async getAttendedEvents(memberId?: number, params?: BaseListRequestParams): Promise<EventsView> {
    const endpoint = memberId ? `events/attended/${memberId}` : 'events/attended';
    const response = await ApiService.get<EventsView>(endpoint, params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches all events (upcoming and past) for a member, sorted by start date descending.
   * @param memberId Member ID
   * @param params Pagination parameters
   */
  static async getMemberEvents(memberId: number, params?: BaseListRequestParams): Promise<EventsView> {
    const response = await ApiService.get<EventsView>(`events/member/${memberId}`, params);
    return plainToClass(EventsView, response);
  }

  /**
   * Fetches event details and maps them to a MemberDetails-compatible object
   * so it can be consumed by ProfileScreen and its child components.
   * @param code Event code
   */
  static async getEventDetails(code: string): Promise<MemberDetails> {
    const response = await ApiService.get<EventDetails>(`events/${code}/details`);
    const event = plainToClass(EventDetails, response);
    return EventsService.mapEventToMemberDetails(event);
  }

  private static mapEventToMemberDetails(event: EventDetails): MemberDetails {
    return plainToClass(MemberDetails, {
      Id: event.Id,
      UserName: event.Code,
      IconUrl: event.IconUrl,
      PhotoUrl: event.PhotoUrl,
      PhotoId: event.PhotoId,
      PlaceName: event.PlaceName,
      CountryCode: event.CountryCode,
      DistanceKm: event.DistanceKm,
      IsFollowing: event.IsFollowing,
      PostsCount: event.PostsCount,
      FollowersCount: event.FollowersCount,
      PublicPhotosCount: event.PublicPhotosCount,
      Description: event.Details,
      EventDetails: event,
    });
  }

  /**
   * Follows an event (interested/watching).
   * @param eventId Event member ID
   */
  static async followEvent(eventId: number): Promise<void> {
    await ApiService.post(`events/${eventId}/follow`, {});
  }

  /**
   * Unfollows an event.
   * @param eventId Event member ID
   */
  static async unfollowEvent(eventId: number): Promise<void> {
    await ApiService.post(`events/${eventId}/unfollow`, {});
  }

  /**
   * Marks current user as attending an event.
   * @param eventId Event member ID
   */
  static async attendEvent(eventId: number): Promise<void> {
    await ApiService.post(`events/${eventId}/attend`, {});
  }

  /**
   * Removes current user's attendance from an event.
   * @param eventId Event member ID
   */
  static async unattendEvent(eventId: number): Promise<void> {
    await ApiService.post(`events/${eventId}/unattend`, {});
  }

  /**
   * Creates a new event.
   * @param data Event creation parameters
   */
  static async createEvent(data: EventCreateParams): Promise<{ Id: number; Code: string }> {
    return ApiService.post('events', data);
  }

  /**
   * Updates an existing event.
   * @param eventId Event member ID
   * @param data Event update parameters
   */
  static async updateEvent(eventId: number, data: EventUpdateParams): Promise<void> {
    await ApiService.put(`events/${eventId}`, data);
  }

  /**
   * Deletes an event.
   * @param eventId Event member ID
   */
  static async deleteEvent(eventId: number): Promise<void> {
    await ApiService.delete(`events/${eventId}`);
  }

  /**
   * Returns the URL for downloading the iCalendar (.ics) file for an event.
   * @param code Event code (username)
   */
  static getICalUrl(code: string): string {
    return ApiService.getUrl(`events/${code}/ical`);
  }
}
